/**
 * Results export button — scope-aware CSV/zip download.
 *
 * Replaces the old DownloadButtonEnhanced for the per-test results page:
 *   - Lets the operator pick what to export (summary, activity rollup,
 *     per-triad F/B/T, or raw responses) or download all four as a zip.
 *   - Streams the result via apiClient.getBlob and triggers a download.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, Loader2 } from 'lucide-react';
import { apiClient } from '../../services/apiClient';
import { showToast } from '../../lib/toast';
import type { ResultsExportScope } from '../../types';

interface Props {
  testId: string;
}

// Activity-level rollup was removed from both the dashboard and the export
// pipeline — see backend csv_export.py. Reintroducing it means re-adding
// `build_activity_csv` and a matching scope here.
const SCOPE_OPTIONS: { value: ResultsExportScope; label: string; description: string }[] = [
  { value: 'all', label: 'All (zip)', description: 'Summary, per-triad detail, and raw responses bundled.' },
  { value: 'summary', label: 'Summary only', description: 'One row per candidate with status.' },
  { value: 'triads', label: 'Per-triad detail', description: 'One row per (candidate, activity, disposition level) with F/B/T maturity.' },
  { value: 'responses', label: 'Raw responses', description: 'One row per (candidate, question, allocated option).' },
];

function triggerBrowserDownload(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export function ResultsExportButton({ testId }: Props) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<ResultsExportScope>('all');
  const [downloading, setDownloading] = useState(false);

  const handleExport = async () => {
    setDownloading(true);
    try {
      const { blob, filename } = await apiClient.getBlob(
        `/api/results/${testId}/export?scope=${scope}`,
      );
      const ext = scope === 'all' ? 'zip' : 'csv';
      triggerBrowserDownload(blob, filename ?? `results-${testId}-${scope}.${ext}`);
      showToast({
        variant: 'success',
        title: 'Export ready',
        description: `Downloaded ${scope === 'all' ? 'zip bundle' : 'CSV'}.`,
      });
      setOpen(false);
    } catch (err) {
      showToast({
        variant: 'error',
        title: 'Export failed',
        description: err instanceof Error ? err.message : 'Could not export results.',
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2"
      >
        <Download size={16} aria-hidden="true" /> Export Results
      </Button>
      <DialogContent className="bg-white rounded-xl shadow-2xl max-w-lg border-brand-steel/50">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-gray-900">Export results</DialogTitle>
          <DialogDescription className="text-sm text-brand-steel">
            Choose what to include. CSV format; the &quot;all&quot; option downloads a zip with one CSV per scope.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {SCOPE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex items-start gap-3 p-3 rounded-lg border border-brand-steel/50 bg-white cursor-pointer hover:bg-brand-panel/50 transition-colors"
            >
              <input
                type="radio"
                name="export-scope"
                value={opt.value}
                checked={scope === opt.value}
                onChange={() => setScope(opt.value)}
                className="mt-1 accent-brand-crimson"
              />
              <span className="flex-1">
                <span className="block text-sm font-medium text-gray-900">{opt.label}</span>
                <span className="block text-xs text-brand-steel">{opt.description}</span>
              </span>
            </label>
          ))}
        </div>
        <DialogFooter className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={downloading}>
            Cancel
          </Button>
          <Button type="button" onClick={handleExport} disabled={downloading}>
            {downloading ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" aria-hidden="true" /> Downloading...
              </>
            ) : (
              <>
                <Download size={16} aria-hidden="true" /> Download
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ResultsExportButton;
